import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { CondominiManager } from '../../lib/condomini-manager';
import { formatTime, nomeUtente } from '../../lib/format';
import { isAdminGlobale } from '../../lib/ruoli';
import {
    useEntityItem, statoLista, Field, DetailPage, DetailActions, ConfirmModal, messaggioErrore
} from './common';
import Select from '../../components/Select';

const VUOTO = {
    name: '', indirizzo: '', citta: '', cap: '',
    referente: '', email_referente: '', telefono_referente: '',
    quiete_inizio: '', quiete_fine: '', attivo: 1
};

/** Cosa si porta via l'eliminazione, nell'ordine in cui lo si legge. */
const CONTENUTI = [
    ['palazzina', 'palazzina', 'palazzine'],
    ['appartamento', 'appartamento', 'appartamenti'],
    ['sala', 'sala', 'sale'],
    ['user', 'utente', 'utenti'],
    ['prenotazione', 'prenotazione', 'prenotazioni']
];

/** "6 palazzine, 60 appartamenti e 2 sale"; stringa vuota se non c'e' nulla. */
export function descriviContenuti(conteggi) {
    const voci = CONTENUTI
        .filter(([chiave]) => conteggi && conteggi[chiave] > 0)
        .map(([chiave, uno, tanti]) => `${conteggi[chiave]} ${conteggi[chiave] === 1 ? uno : tanti}`);
    if (voci.length <= 1) return voci.join('');
    return `${voci.slice(0, -1).join(', ')} e ${voci[voci.length - 1]}`;
}

const ADMIN_VUOTO = { name: '', surname: '', email: '', telefono: '', password: '' };

/** Nome, cognome, email e password non possono mancare a un amministratore. */
function amministratoreIncompleto(a) {
    return !a.name.trim() || !a.surname.trim() || !a.email.trim() || !a.password;
}

/**
 * I cinque campi di un amministratore: gli stessi alla nascita del condominio
 * e quando se ne nomina un altro dopo.
 */
function CampiAmministratore({ valori, onChange }) {
    return (
        <div className="lv-form-grid">
            <Field label="Nome" required>
                <input className="lv-input" value={valori.name} onChange={e => onChange('name', e.target.value)} />
            </Field>
            <Field label="Cognome" required>
                <input className="lv-input" value={valori.surname} onChange={e => onChange('surname', e.target.value)} />
            </Field>
            <Field label="Email" required hint="È anche il nome utente con cui accede.">
                <input
                    type="email"
                    className="lv-input"
                    value={valori.email}
                    onChange={e => onChange('email', e.target.value)}
                />
            </Field>
            <Field label="Telefono">
                <input className="lv-input" value={valori.telefono} onChange={e => onChange('telefono', e.target.value)} />
            </Field>
            <Field label="Password iniziale" required>
                <input
                    type="password"
                    className="lv-input"
                    value={valori.password}
                    onChange={e => onChange('password', e.target.value)}
                />
            </Field>
        </div>
    );
}

/**
 * Amministratori di un condominio gia' esistente.
 *
 * L'elenco lo vede chiunque apra la pagina; la nomina e' riservata
 * all'amministratore globale, l'unico che possa scrivere un utente fuori dal
 * proprio gruppo, e passa da `add_amministratore`. Chi amministra un
 * condominio i suoi co-amministratori li nomina dall'anagrafica utenti,
 * alzando il ruolo di qualcuno che sta gia' nel gruppo.
 *
 * Senza questa sezione un condominio che perde il suo unico amministratore
 * resterebbe irraggiungibile dall'applicazione.
 */
function Amministratori({ idGruppo }) {
    const utente = LoginManager.getUserInfo();
    const globale = isAdminGlobale(utente);

    const [amministratori, setAmministratori] = useState(null);
    const [erroreElenco, setErroreElenco] = useState(null);
    const [nuovoAdmin, setNuovoAdmin] = useState(null);
    const [salvando, setSalvando] = useState(false);

    const carica = useCallback(async () => {
        setErroreElenco(null);
        try {
            const res = await CondominiManager.listAmministratori(idGruppo);
            if (res && res.success === 1) setAmministratori(res.body || []);
            else setErroreElenco(messaggioErrore(res, 'Impossibile caricare gli amministratori.'));
        }
        catch (err) {
            setErroreElenco('Impossibile contattare il server.');
        }
    }, [idGruppo]);

    useEffect(() => { carica(); }, [carica]);

    async function nomina() {
        if (amministratoreIncompleto(nuovoAdmin)) {
            toast.error('Nome, cognome, email e password del nuovo amministratore sono obbligatori.', { theme: 'colored' });
            return;
        }
        setSalvando(true);
        try {
            const res = await CondominiManager.addAmministratore(idGruppo, {
                name: nuovoAdmin.name.trim(),
                surname: nuovoAdmin.surname.trim(),
                email: nuovoAdmin.email.trim(),
                telefono: nuovoAdmin.telefono.trim(),
                password: nuovoAdmin.password
            });
            if (res && res.success === 1) {
                toast.success(res.message || 'Amministratore nominato.', { theme: 'colored' });
                setNuovoAdmin(null);
                carica();
            }
            else {
                toast.error(messaggioErrore(res, 'Nomina non riuscita.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSalvando(false);
        }
    }

    return (
        <div className="lv-subsection">
            <div className="lv-subsection-head">
                <h3 className="lv-subsection-title">Amministratori</h3>
                {globale && !nuovoAdmin && (
                    <button
                        type="button"
                        className="lv-btn lv-btn-ghost lv-btn-sm"
                        onClick={() => setNuovoAdmin({ ...ADMIN_VUOTO })}
                    >
                        <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
                        Aggiungi amministratore
                    </button>
                )}
            </div>

            {erroreElenco && <div className="lv-note">{erroreElenco}</div>}

            {!erroreElenco && amministratori && (
                amministratori.length === 0 ? (
                    <div className="lv-note">
                        Nessun amministratore: nel condominio non c&apos;è nessuno che possa
                        creare sale, palazzine e appartamenti.
                    </div>
                ) : (
                    <div className="lv-table-wrap">
                        <table className="lv-table">
                            <thead>
                                <tr>
                                    <th>Amministratore</th>
                                    <th>Email</th>
                                    <th>Telefono</th>
                                </tr>
                            </thead>
                            <tbody>
                                {amministratori.map(u => (
                                    <tr key={u.id}>
                                        <td data-label="Amministratore">{nomeUtente(u)}</td>
                                        <td data-label="Email">{u.email}</td>
                                        <td data-label="Telefono">{u.telefono || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}

            {nuovoAdmin && (
                <div className="lv-subform">
                    <CampiAmministratore
                        valori={nuovoAdmin}
                        onChange={(campo, valore) => setNuovoAdmin(prev => ({ ...prev, [campo]: valore }))}
                    />
                    <div className="lv-subform-actions">
                        {/* niente <form> annidato: la pagina ne ha gia' uno per il condominio */}
                        <button
                            type="button"
                            className="lv-btn lv-btn-primary lv-btn-sm"
                            onClick={nomina}
                            disabled={salvando}
                        >
                            {salvando && <FontAwesomeIcon icon={['fas', 'circle-notch']} spin className="me-2" />}
                            Nomina amministratore
                        </button>
                        <button
                            type="button"
                            className="lv-btn lv-btn-ghost lv-btn-sm"
                            onClick={() => setNuovoAdmin(null)}
                            disabled={salvando}
                        >
                            Annulla
                        </button>
                    </div>
                </div>
            )}

            {!globale && (
                <div className="lv-note mt-3">
                    <FontAwesomeIcon icon={['fas', 'circle-info']} className="me-2" />
                    Per nominarne un altro alza a «Amministratore condominio» il ruolo di un
                    utente del condominio, da <Link to="/admin/utenti">Utenti</Link>.
                </div>
            )}
        </div>
    );
}

/**
 * Pagina di un condominio: dati identificativi, referente e fascia di quiete.
 *
 * Creandone uno nuovo si chiede anche il suo primo amministratore: senza,
 * il condominio resterebbe una scatola che nessuno puo' riempire, perche' la
 * CRUD del framework timbra ogni inserimento con il gruppo di chi scrive.
 *
 * I parametri operativi della prenotazione (finestra, anticipo, annullamento,
 * limite mensile) vivono invece sulla singola sala, perche' e' li' che il
 * BookingEngine li legge.
 */
export default function Condominio({ idGruppo, senzaElenco }) {
    const { id } = useParams();
    const navigate = useNavigate();

    const utente = LoginManager.getUserInfo();
    const globale = isAdminGlobale(utente);
    const idEffettivo = idGruppo || id;
    const nuovo = !idEffettivo || idEffettivo === 'nuovo';
    const { item, loading, error } = useEntityItem('user_group', nuovo ? null : idEffettivo);

    const [form, setForm] = useState(nuovo ? { ...VUOTO } : null);
    const [admin, setAdmin] = useState({ ...ADMIN_VUOTO });
    const [saving, setSaving] = useState(false);
    // null = conferma chiusa; altrimenti i conteggi di cio' che sparira'
    const [daEliminare, setDaEliminare] = useState(null);
    const [working, setWorking] = useState(false);

    /* Elimina solo l'amministratore globale, dall'elenco dei condomini, e mai
       il condominio del proprio utente: lo stesso rifiuto lo da' il server. */
    const eliminabile = !nuovo && globale && !senzaElenco && Boolean(item)
        && String(item.id) !== String(utente && utente.idUserGroup);

    useEffect(() => {
        if (!item) return;
        setForm({
            name: item.name || '',
            indirizzo: item.indirizzo || '',
            citta: item.citta || '',
            cap: item.cap || '',
            referente: item.referente || '',
            email_referente: item.email_referente || '',
            telefono_referente: item.telefono_referente || '',
            quiete_inizio: item.quiete_inizio ? formatTime(item.quiete_inizio) : '',
            quiete_fine: item.quiete_fine ? formatTime(item.quiete_fine) : '',
            attivo: parseInt(item.attivo, 10) === 0 ? 0 : 1
        });
    }, [item]);

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    function aggiornaAdmin(campo, valore) {
        setAdmin(prev => ({ ...prev, [campo]: valore }));
    }

    async function salva(e) {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error('Il nome del condominio è obbligatorio.', { theme: 'colored' });
            return;
        }
        const soloUnOrario = Boolean(form.quiete_inizio) !== Boolean(form.quiete_fine);
        if (soloUnOrario) {
            toast.error('Indica entrambi gli orari della fascia di quiete, oppure lasciali vuoti.', { theme: 'colored' });
            return;
        }
        if (nuovo && amministratoreIncompleto(admin)) {
            toast.error('Nome, cognome, email e password del primo amministratore sono obbligatori.', { theme: 'colored' });
            return;
        }

        setSaving(true);
        try {
            if (nuovo) {
                const res = await CondominiManager.createCondominio(
                    {
                        name: form.name.trim(),
                        indirizzo: form.indirizzo,
                        citta: form.citta,
                        cap: form.cap,
                        referente: form.referente,
                        email_referente: form.email_referente,
                        telefono_referente: form.telefono_referente,
                        quiete_inizio: form.quiete_inizio,
                        quiete_fine: form.quiete_fine
                    },
                    {
                        name: admin.name.trim(),
                        surname: admin.surname.trim(),
                        email: admin.email.trim(),
                        telefono: admin.telefono.trim(),
                        password: admin.password
                    }
                );
                if (res && res.success === 1) {
                    toast.success(res.message || 'Condominio creato.', { theme: 'colored' });
                    navigate('/admin/impostazioni');
                }
                else {
                    toast.error(messaggioErrore(res, 'Creazione non riuscita.'), { theme: 'colored' });
                }
            }
            else {
                const payload = {
                    name: form.name.trim(),
                    indirizzo: form.indirizzo,
                    citta: form.citta,
                    cap: form.cap,
                    referente: form.referente,
                    email_referente: form.email_referente,
                    telefono_referente: form.telefono_referente,
                    quiete_inizio: form.quiete_inizio ? `${form.quiete_inizio}:00` : '',
                    quiete_fine: form.quiete_fine ? `${form.quiete_fine}:00` : '',
                    attivo: parseInt(form.attivo, 10)
                };
                const res = await DataManager.updateItem('user_group', payload, item.id);
                if (res && res.success === 1) {
                    toast.success('Impostazioni salvate.', { theme: 'colored' });
                    if (!senzaElenco) navigate('/admin/impostazioni');
                }
                else {
                    toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
                }
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function chiediEliminazione() {
        setWorking(true);
        try {
            const res = await CondominiManager.riepilogoCondominio(item.id);
            if (res && res.success === 1) {
                setDaEliminare(res.body.conteggi || {});
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await CondominiManager.deleteCondominio(item.id);
            if (res && res.success === 1) {
                toast.success(res.message || 'Condominio eliminato.', { theme: 'colored' });
                navigate('/admin/impostazioni');
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    const contenuti = descriviContenuti(daEliminare);

    const stato = statoLista({
        loading: loading,
        error: error || (!idEffettivo && !nuovo ? 'Il tuo utente non è associato ad alcun condominio.' : null),
        empty: !form,
        icon: 'building-user'
    });

    return (
        <DetailPage
            backTo={senzaElenco ? null : '/admin/impostazioni'}
            backLabel="Torna ai condomini"
            eyebrow="Configurazione"
            title={nuovo ? 'Nuovo condominio' : (item ? item.name : 'Condominio')}
            subtitle="Dati del condominio e fascia di quiete diurna."
        >
            {stato || (
                <form onSubmit={salva}>
                    <div className="lv-form-grid">
                        <Field label="Nome del condominio" required>
                            <input className="lv-input" value={form.name} onChange={e => aggiorna('name', e.target.value)} />
                        </Field>
                        <Field label="Indirizzo">
                            <input
                                className="lv-input"
                                value={form.indirizzo}
                                onChange={e => aggiorna('indirizzo', e.target.value)}
                            />
                        </Field>
                        <Field label="Città">
                            <input className="lv-input" value={form.citta} onChange={e => aggiorna('citta', e.target.value)} />
                        </Field>
                        <Field label="CAP">
                            <input className="lv-input" value={form.cap} onChange={e => aggiorna('cap', e.target.value)} />
                        </Field>
                        <Field label="Referente">
                            <input
                                className="lv-input"
                                value={form.referente}
                                onChange={e => aggiorna('referente', e.target.value)}
                            />
                        </Field>
                        <Field label="Email del referente">
                            <input
                                type="email"
                                className="lv-input"
                                value={form.email_referente}
                                onChange={e => aggiorna('email_referente', e.target.value)}
                            />
                        </Field>
                        <Field label="Telefono del referente">
                            <input
                                className="lv-input"
                                value={form.telefono_referente}
                                onChange={e => aggiorna('telefono_referente', e.target.value)}
                            />
                        </Field>
                        <Field label="Inizio fascia di quiete" hint="Orario di riposo diurno comunicato ai condomini.">
                            <input
                                type="time"
                                className="lv-input"
                                value={form.quiete_inizio}
                                onChange={e => aggiorna('quiete_inizio', e.target.value)}
                            />
                        </Field>
                        <Field label="Fine fascia di quiete">
                            <input
                                type="time"
                                className="lv-input"
                                value={form.quiete_fine}
                                onChange={e => aggiorna('quiete_fine', e.target.value)}
                            />
                        </Field>
                        {/* sospendere un condominio e' cosa dell'amministratore
                            globale: chi ne amministra uno non puo' chiudere il
                            proprio, e alla nascita nasce sempre attivo */}
                        {!nuovo && globale && (
                            <Field label="Stato" hint="Lo stato che compare nell'elenco dei condomini.">
                                <Select
                                    value={form.attivo}
                                    onChange={v => aggiorna('attivo', v)}
                                    options={[
                                        { value: '1', label: 'Attivo' },
                                        { value: '0', label: 'Sospeso' }
                                    ]}
                                />
                            </Field>
                        )}
                    </div>

                    {nuovo && (
                        <div className="lv-subsection">
                            <div className="lv-subsection-head">
                                <h3 className="lv-subsection-title">Primo amministratore</h3>
                            </div>
                            <div className="lv-note mb-3">
                                Sarà lui a creare sale, palazzine e condomini del nuovo condominio:
                                l&apos;amministratore globale non può farlo al posto suo.
                            </div>
                            <CampiAmministratore valori={admin} onChange={aggiornaAdmin} />
                        </div>
                    )}

                    <DetailActions saving={saving} submitLabel={nuovo ? 'Crea condominio' : 'Salva impostazioni'}>
                        {eliminabile && (
                            <button
                                type="button"
                                className="lv-btn lv-btn-ghost lv-btn-danger-ghost"
                                onClick={chiediEliminazione}
                                disabled={working}
                            >
                                Elimina
                            </button>
                        )}
                    </DetailActions>

                    <div className="lv-note mt-3">
                        <FontAwesomeIcon icon={['fas', 'circle-info']} className="me-2" />
                        Finestra di prenotazione, anticipo minimo, termine di annullamento e limite mensile
                        si impostano sulla singola sala, in <Link to="/admin/sale">Sale</Link>: sono quelli
                        che il motore di prenotazione applica alle richieste.
                    </div>
                </form>
            )}

            {/* fuori dal <form>: la nomina e' una chiamata a se', non passa dal
                salvataggio dei dati del condominio */}
            {!stato && !nuovo && item && <Amministratori idGruppo={item.id} />}

            <ConfirmModal
                show={daEliminare !== null}
                title="Eliminare il condominio?"
                message={item
                    ? `«${item.name}» sparirà insieme a tutto ciò che contiene${contenuti ? `: ${contenuti}` : ''}. `
                        + 'Gli utenti non potranno più accedere.'
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(null)}
            />
        </DetailPage>
    );
}
